/* eslint-disable react/jsx-boolean-value */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Grid,
  Typography,
} from '@material-ui/core'
import { Loader } from '@refera/ui-web'

import Accordion from '_/components/accordion'
import HelpInfoButton from '_/components/button/help-info-button'

import { InputField, InputFieldMask, PhoneInputField } from '../textfield'
import useStyles from '../styles'
import { useDispatch, useSelector } from 'react-redux'
import { financeOptionsSelector } from '_/modules/finance/selectors'
import { providerSelector } from '_modules/provider/selectors'
import useIsGenericParamActive from '_hooks/use-is-generic-param-active'
import { GENERIC_PARAMETERS } from '_utils/constants/service-order'

import AddIcon from '@material-ui/icons/Add'
import CardMedia from '_components/card-media'
import DefaultImage from '_assets/images/default.png'

import SwitchComponent from '_/components/switch'
import { CPF_CNPJ_REGEX, ROUTES } from '_/utils/constants'
import { ControlledCpfCnpjInput } from '_/components/inputs/MaskInput/common/CpfCnpjInput'
import {
  validateProviderCpfCnpj,
  patchAgencyProviderLink,
  deleteAgencyProviderLink,
} from '_/modules/provider/actions'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'
import { userSelector } from '_/modules/authentication/selectors'
import { BasicInput, MaskBasicInput } from '_/components/inputs'
import classnames from 'classnames'
import { validationCep } from '_/utils/helpers'
import { useLocation, navigate } from '@reach/router'
import { parse } from 'query-string'
import Button from '_components/button'
import moment from 'moment'

export const CompanyAside = ({ register, isEditing, control, provider }) => {
  const styles = useStyles()
  const [displayStatus, setDisplayStatus] = useState()
  const { setValue, watch } = useFormContext()

  const logoValue = watch('brandLogo')

  const onChooseFile = useCallback(
    event => {
      const newFile = event.target.files[0]
      if (newFile) {
        setValue('brandLogo', newFile)
      }
    },
    [setValue, watch]
  )

  const handleMouseOver = useCallback(() => {
    if (isEditing) setDisplayStatus(true)
  }, [isEditing])

  const handleMouseOut = useCallback(() => {
    if (isEditing) setDisplayStatus(false)
  }, [isEditing])

  return (
    <>
      <label
        onMouseOver={handleMouseOver}
        onFocus={handleMouseOver}
        onMouseOut={handleMouseOut}
        onBlur={handleMouseOut}
        htmlFor="upload-image"
        className={styles.addNewPictureContainer}
      >
        <input
          onChange={onChooseFile}
          id="upload-image"
          type="file"
          accept="image/*"
          key="input"
          className={styles.fileSelector}
          disabled={!isEditing}
        />
        {displayStatus ? (
          <Grid container justify="center" alignItems="center" className={styles.addNewPicture}>
            <AddIcon className={styles.addIcon} />
          </Grid>
        ) : (
          <CardMedia
            className={styles.image}
            file={logoValue || provider?.brandLogo || DefaultImage}
            alt="Imagem da empresa"
            showOnly
          />
        )}
      </label>
      <InputField
        label="Nome da empresa"
        name="name"
        inputRef={register}
        disabled={!isEditing}
        placeholder="Digite o nome da empresa"
        required
      />
      <InputField
        label="Nome do responsável"
        name="person_responsible_name"
        disabled={!isEditing}
        placeholder="Nome completo do responsável"
        inputRef={register}
        required
      />
      <PhoneInputField
        label="Telefone"
        name="phone"
        control={control}
        placeholder="(00) 0 0000-0000"
        isEditing={isEditing}
        required
      />
      <InputField
        label="E-mail da empresa"
        type="email"
        name="contact_email"
        inputRef={register}
        disabled={!isEditing}
        placeholder="Digite o e-mail"
        help="E-mail utilizado pela empresa para receber notificações via messageria. (não é o de login)"
        required
      />
    </>
  )
}

export const CompanyPrincipalData = ({ isEditing, data, setValue }) => {
  const styles = useStyles()
  const isEmergencyManagementActive = useIsGenericParamActive({
    name: GENERIC_PARAMETERS.EMERGENCY_MANAGEMENT,
  })

  return (
    <Accordion title="Dados principais" className={styles.accordion}>
      {isEmergencyManagementActive && (
        <FormControl>
          <InputLabel variant="standard" id="priority_for_emergencies" shrink>
            Prioridade para emergências
          </InputLabel>
          <FormControlLabel
            className={styles.switch}
            control={
              <SwitchComponent
                label="Prioridade para emergências"
                checked={!!data?.priority_for_emergencies}
                onChange={e => setValue('priority_for_emergencies', e.target.checked)}
                disabled={!isEditing}
              />
            }
          />
        </FormControl>
      )}
    </Accordion>
  )
}

export const ProviderCurrentStatus = () => {
  const styles = useStyles()
  const { showDialog, closeDialog } = useDialog()
  const provider = useSelector(providerSelector)
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const user = useSelector(userSelector)
  const agencyId = user?.agency?.id ?? user?.agency
  const [isSaving, setIsSaving] = useState(false)
  const isPaused = !!provider?.paused
  const actionLabel = isPaused ? 'Ativar Prestador' : 'Pausar Prestador'

  const handlePauseClick = async () => {
    const newPaused = !isPaused
    if (!agencyId) {
      showToast({
        type: 'error',
        message: 'Intermediária não encontrada para atualizar o Prestador.',
      })
      return
    }
    try {
      setIsSaving(true)
      const res = await dispatch(
        patchAgencyProviderLink(provider?.id, agencyId, { paused: newPaused })
      )
      showToast({
        type: 'success',
        message: res?.detail || (newPaused ? 'Prestador pausado' : 'Prestador reativado'),
      })
    } catch (error) {
      showToast({
        type: 'error',
        message: error?.error_message || 'Erro ao atualizar status do prestador',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDeleteClick = () => {
    showDialog({
      type: 'warning',
      subject:
        'Voce tem certeza que deseja excluir esse prestador? Ao clicar em excluir, o prestador sai da sua base de prestadores para sempre e não aparecerá mais nos chamados',
      labelApprove: 'Sim',
      labelCancel: 'Não',
      onApprove: async () => {
        if (!agencyId) {
          showToast({
            type: 'error',
            message: 'Intermediária não encontrada para excluir o vínculo.',
          })
          return
        }
        try {
          setIsSaving(true)
          await dispatch(deleteAgencyProviderLink(provider?.id, agencyId))
          showToast({ type: 'success', message: 'Prestador excluído com sucesso.' })
          navigate(ROUTES.MANAGE_PROVIDERS)
        } catch (error) {
          showToast({ type: 'error', message: error?.error_message || 'Erro ao excluir prestador' })
        } finally {
          setIsSaving(false)
          closeDialog()
        }
      },
      onCancel: closeDialog,
    })
  }

  return (
    <Accordion
      title="Status Atual"
      className={classnames(styles.accordionFullWidth, styles.statusDetailsFullWidth)}
    >
      <Grid item className={styles.statusRow}>
        <Grid container alignItems="center" style={{ gap: 8 }}>
          <Typography variant="body1">
            {`Status: ${isPaused ? 'Prestador pausado' : 'Prestador Ativado'}`}
          </Typography>
          <HelpInfoButton title="Ao clicar em Pausar, você impede que o prestador apareça na listagem de seleção para novos chamados. Clicando em Ativar prestador, o prestador volta a aparecer na listagem de chamados" />
        </Grid>
        <Typography variant="body1">
          {`Data do cadastro: ${
            provider?.providerSignupDate
              ? moment(provider.providerSignupDate).format('DD/MM/YYYY')
              : provider?.createdAt
              ? moment(provider.createdAt).format('DD/MM/YYYY')
              : '-'
          }`}
        </Typography>
        <div className={styles.actionsRow}>
          <Button
            variant="outlined"
            onClick={handlePauseClick}
            disabled={isSaving}
            color={isPaused ? 'green' : 'primary'}
          >
            {actionLabel}
          </Button>
          <Button
            className={styles.deleteAlignRight}
            variant="outlined"
            color="red"
            onClick={handleDeleteClick}
          >
            Excluir Prestador
          </Button>
        </div>
      </Grid>
    </Accordion>
  )
}

export const CPF_REGEX = /^\d{3}\.\d{3}\.\d{3}-\d{2}$/

export const CompanyData = ({ isEditing, control, data }) => {
  const styles = useStyles()
  const taxRegimeOptions = useSelector(financeOptionsSelector)?.taxRegimes
  const { watch, errors, setValue } = useFormContext()
  const provider = useSelector(providerSelector)
  const dispatch = useDispatch()
  const { showToast } = useToast()
  const [loading, setLoading] = useState(false)
  const [cpfCnpjToValidate, setCpfCnpjToValidate] = useState('')
  const menuProps = useMemo(
    () => ({
      classes: { paper: styles.subjectField },
      getContentAnchorEl: null,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'center',
      },
    }),
    []
  )
  const { search } = useLocation()

  const handleCpfCnpjBlur = useCallback(() => {
    if (errors?.cnpj) return

    const value = watch('cnpj')
    const onlyNumbers = value.replace(/\D/g, '') || ''

    if (onlyNumbers === provider?.cnpj || value === cpfCnpjToValidate) return

    setCpfCnpjToValidate(value)
  }, [errors?.cnpj, cpfCnpjToValidate, provider?.cnpj])

  useEffect(() => {
    if (cpfCnpjToValidate) {
      const actionType = provider?.id ? 'update' : 'create'
      const queryAgency = parse(search)?.intermediaria
      const params = {
        type: actionType,
        cpf_or_cnpj: watch('cnpj'),
      }

      // refera creating provider for agency
      if (queryAgency) {
        params.agency_id = queryAgency
        params.refera_creating_for_agency = true
      }

      setLoading(true)

      dispatch(validateProviderCpfCnpj(params))
        .then(response => {
          if (response?.message)
            showToast({
              type: 'info',
              message: response?.message,
            })
          setLoading(false)
        })
        .catch(error => {
          showToast({
            type: 'error',
            message: error?.error_message || 'Erro ao validar CPF/CNPJ',
          })
          setLoading(false)
        })
    }
  }, [cpfCnpjToValidate, provider?.id])

  const isValidCpf = useMemo(() => {
    const cpfValue = watch('cnpj') || ''
    const valid = CPF_REGEX.test(cpfValue)

    if (valid) {
      setValue('tax_regime', null)
    } else {
      const currentValue = watch('tax_regime')
      if (currentValue === null || currentValue === undefined) {
        setValue('tax_regime', '')
      }
    }

    return valid
  }, [watch('cnpj'), watch('tax_regime'), setValue])

  return (
    <Accordion title="Dados da empresa" className={styles.accordion}>
      <Loader hasBackdrop open={loading} label="Aguarde..." />
      <ControlledCpfCnpjInput
        name="cnpj"
        label="CPF/CNPJ"
        showErrorMessage
        errorMessage={errors?.cnpj?.message}
        className={styles.cpfCnpjField}
        controllerProps={{
          rules: {
            pattern: {
              value: CPF_CNPJ_REGEX,
              message: 'CPF/CNPJ inválido',
            },
          },
        }}
        fieldProps={{
          error: !!errors?.cnpj,
          disabled: !isEditing || !!provider?.id,
          onBlur: handleCpfCnpjBlur,
        }}
        required
        defaultValue={provider?.cnpj}
      />
      <FormControl>
        <InputLabel variant="standard" id="tax_regime" shrink>
          Regime tributário
        </InputLabel>
        <Controller
          control={control}
          name="tax_regime"
          defaultValue={isValidCpf ? null : data?.taxRegime || ''}
          as={
            <Select
              MenuProps={menuProps}
              labelId="tax-regime"
              disabled={!isEditing || isValidCpf}
              required={!isValidCpf}
            >
              {taxRegimeOptions?.map(object => {
                return (
                  <MenuItem key={object.label} value={object.value}>
                    {object.label}
                  </MenuItem>
                )
              })}
            </Select>
          }
        />
      </FormControl>
      <InputFieldMask
        label="Data do cadastro"
        name="company_creation_date"
        control={control}
        mask="99/99/9999"
        placeholder="DD/MM/AAAA"
        isEditing={isEditing}
      />
      <InputFieldMask
        label="Número de funcionários"
        name="number_of_employees"
        control={control}
        mask="9999"
        placeholder="0000"
        isEditing={isEditing}
      />
    </Accordion>
  )
}

export const CompanyAddress = ({ isEditing }) => {
  const styles = useStyles()
  const { errors } = useFormContext()

  return (
    <Accordion
      title="Endereço da empresa"
      className={classnames(styles.accordion, styles.accordionGap)}
    >
      <BasicInput
        label="Endereço"
        name="address"
        disabled={!isEditing}
        placeholder="Digite o endereço"
      />
      <BasicInput
        label="Número"
        name="street_number"
        rules={{ maxLength: 64 }}
        disabled={!isEditing}
        placeholder="0000"
      />
      <MaskBasicInput
        label="CEP"
        name="cep"
        mask="99999-999"
        placeholder="00000-000"
        rules={{
          validationCep,
          maxLength: 9,
        }}
        disabled={!isEditing}
        labelClasses={styles.cepLabel}
        containerClasses={styles.cepContainer}
        showErrorMessage={!!errors?.cep?.message}
      />
      <BasicInput
        label="Complemento"
        name="extra_address_info"
        disabled={!isEditing}
        placeholder="Digite o complemento"
      />
      <BasicInput
        label="Bairro"
        name="neighborhood"
        disabled={!isEditing}
        placeholder="Digite o bairro"
      />
      <BasicInput label="Cidade" name="city" disabled={!isEditing} placeholder="Digite a cidade" />
      <BasicInput
        label="Ponto de referência"
        name="nearby_landmark"
        disabled={!isEditing}
        placeholder="Digite uma referência"
      />
      <BasicInput label="Estado" name="state" disabled={!isEditing} placeholder="Digite o estado" />
    </Accordion>
  )
}
